import React, { useState } from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { propTypesComponentPaymentInput } from '../../shared.propTypes';
import styles from './PaymentInputCreditCardLegacy.module.css';
import stylesShared from '../../shared.module.css';
import CheckBox from '../../../CheckBox/CheckBox';
import Button from '../../../Button/Button';
import Input from '../../../Input/Input';
import { cardExpirationFormatValidation } from '../../../../shared/utilities/common';
import PaymentInputCreditCardSavedList from '../PaymentInputCreditCardSavedList/PaymentInputCreditCardSavedList';
import { keyDownA11y } from '../../shared';
import {
  getCheckoutCheckIconSrc,
  getCheckoutPartProps,
  isReviewOrderBrandRefresh,
} from '../../../../pages/ReviewOrder/reviewOrderBrandRefreshClasses';

const brandRefreshCardNumberIcon = require('./imgs/brand-refresh-card-number-icon.svg');
const brandRefreshLockIcon = require('./imgs/brand-refresh-lock.svg');

const inputMasks = {
  cc19: '#### #### #### #######',
  cc15: '#### ###### #####',
  ccCvv: '####',
  ccExpiration: '##/##',
  ccZip: '#####',
};

const useCreditCardLegacyForm = (
  storePaymentMethod,
  callback,
  isSavePaymentMethodCheckedAndDisabled,
  inputsInitial
) => {
  const [inputs, setInputs] = useState(inputsInitial || {});

  const handleSubmit = event => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    const paymentCredentialSubmitted = {
      summary: `Card ending in ${inputs.cardNumber
        .trimEnd()
        .substr(inputs.cardNumber.trimEnd().length - 4)}`,
      storePaymentMethod,
      paymentCredential: {
        paymentMethodId: storePaymentMethod.paymentMethodId,
        credential: inputs.cardNumber.trimEnd(),
        securityCode: inputs.cvv,
        data: {
          cardHolderName: inputs.cardHolderName,
          expirationDate: inputs.expirationDate,
          postcode: inputs.postcode
        },
        saveCredentials: inputs.saveCard || isSavePaymentMethodCheckedAndDisabled
      }
    };
    callback(paymentCredentialSubmitted);
  };

  const handleInputChange = event => {
    if (event.persist) {
      event.persist();
    }
    const newInputs = { ...inputs, [event.target.name]: event.target.value };
    setInputs(newInputs);
  };

  return {
    handleSubmit,
    handleInputChange,
    inputs
  };
};

const views = {
  newCard: 1,
  savedCards: 2
};

const PaymentInputCreditCardLegacy = props => {
  const {
    storePaymentMethod,
    paymentCredentialSubmitted,
    isSavePaymentMethodAllowed,
    isSavePaymentMethodCheckedAndDisabled,
    onSubmitPaymentCredentials,
    onCancel,
    hideCancel,
    paymentMethodsCustomerSaved,
    inputsInitial
  } = props;

  const brandRefresh = isReviewOrderBrandRefresh();
  const hasSavedCards = paymentMethodsCustomerSaved && paymentMethodsCustomerSaved.length > 0;
  const [maskCc] = useState(inputMasks.cc19);
  const [view, setView] = useState(hasSavedCards ? views.savedCards : views.newCard);
  const { inputs, handleInputChange, handleSubmit } = useCreditCardLegacyForm(
    storePaymentMethod,
    onSubmitPaymentCredentials,
    isSavePaymentMethodCheckedAndDisabled,
    inputsInitial
  );
  const isEdit =
    inputs.cardNumber &&
    paymentCredentialSubmitted?.paymentCredential?.credential === inputs.cardNumber;
  const theme = isEdit ? 'Light' : 'Dark';
  const isRequired = !paymentCredentialSubmitted;
  const onCancelCc = () => {
    if (onCancel) {
      if (hasSavedCards) {
        setView(views.savedCards);
      }
      onCancel();
    }
  };

  const expirationLabel = brandRefresh ? 'Expiration Date (MM/YY)' : 'Expiration Date (MM/YY)';
  const securityLabel = brandRefresh ? 'Security Code' : 'CVV';
  const postalLabel = brandRefresh ? 'Postal Code' : 'Billing Zipcode';
  const submitLabel = (() => {
    if (paymentCredentialSubmitted) {
      return 'Save Changes';
    }
    return brandRefresh ? 'Add card' : 'Add card';
  })();

  const cardNumberInput = (
    <Input
      theme={theme}
      label="Card Number"
      name="cardNumber"
      value={inputs.cardNumber}
      isRequired={isRequired}
      inputMask={maskCc}
      isNumberFormat
      handleInput={handleInputChange}
      customContainerStyles={styles.inputContainer}
    />
  );

  const brandCardNumberField = (
    <div
      className={styles.brandCardNumberShell}
      data-part={getCheckoutPartProps('legacy-card-number-field')}
    >
      <img
        alt=""
        aria-hidden="true"
        className={styles.brandFieldIconLeading}
        src={brandRefreshCardNumberIcon}
        width={20}
        height={20}
      />
      <div className={styles.brandCardNumberContent}>
          <NumberFormat
          id="card-number"
          name="cardNumber"
          data-part={getCheckoutPartProps('legacy-card-number-input')}
          className={[
            styles.brandCardNumberControl,
            inputs.cardNumber ? styles.brandCardNumberControlFilled : null,
          ].join(' ')}
          value={inputs.cardNumber || ''}
          format={maskCc}
          onChange={handleInputChange}
          required={isRequired}
          aria-required={isRequired}
          autoComplete="cc-number"
          placeholder=" "
        />
        <label className={styles.brandCardNumberLabel} htmlFor="card-number">
          Card Number
          {isRequired ? <span className={styles.brandRequiredMark}>*</span> : null}
        </label>
      </div>
      <img
        alt=""
        aria-hidden="true"
        className={styles.brandFieldIconTrailing}
        src={brandRefreshLockIcon}
        width={20}
        height={20}
      />
    </div>
  );

  return (
    <>
      <PaymentInputCreditCardSavedList
        storePaymentMethod={storePaymentMethod}
        paymentCredentialSubmitted={paymentCredentialSubmitted}
        onCancel={onCancelCc}
        hideCancel={hideCancel}
        paymentMethodsCustomerSaved={paymentMethodsCustomerSaved}
        hidden={view !== views.savedCards}
        onSubmitPaymentCredentials={onSubmitPaymentCredentials}
        onAddNewCardClick={() => {
          setView(views.newCard);
        }}
      />
      <div
        hidden={view !== views.newCard}
        className={styles.secondaryAddressContainer}
        data-part={getCheckoutPartProps('legacy-add-card')}
      >
        {brandRefresh ? (
          <h4
            className={styles.brandAddNewCardTitle}
            data-part={getCheckoutPartProps('legacy-add-card-title')}
          >
            Add New Card
          </h4>
        ) : null}
        {!brandRefresh && !paymentCredentialSubmitted ? (
          <span className={styles.selectionMinor}>*Indicates required fields</span>
        ) : null}
        <form data-part={getCheckoutPartProps('legacy-add-card-form')}>
          <Input
            theme={theme}
            label="Cardholder Name"
            name="cardHolderName"
            value={inputs.cardHolderName}
            isRequired={isRequired}
            handleInput={handleInputChange}
            customContainerStyles={styles.inputContainer}
          />
          {brandRefresh ? brandCardNumberField : cardNumberInput}
          <div
            className={
              brandRefresh ? styles.brandStackedFields : styles.secondaryInputRow
            }
          >
            <Input
              theme={theme}
              label={expirationLabel}
              name="expirationDate"
              value={inputs.expirationDate}
              isRequired={isRequired}
              inputMask={inputMasks.ccExpiration}
              isNumberFormat
              handleInput={handleInputChange}
              isAllowed={cardExpirationFormatValidation}
              customContainerStyles={
                brandRefresh ? styles.inputContainer : styles.customExpirationDate
              }
            />
            <Input
              theme={theme}
              label={securityLabel}
              name="cvv"
              value={inputs.cvv}
              isRequired={isRequired}
              inputMask={inputMasks.ccCvv}
              isNumberFormat
              handleInput={handleInputChange}
              customContainerStyles={
                brandRefresh ? styles.inputContainer : styles.customCVV
              }
            />
          </div>

          <Input
            theme={theme}
            label={postalLabel}
            name="postcode"
            value={inputs.postcode}
            isRequired={isRequired}
            inputMask={inputMasks.ccZip}
            isNumberFormat
            handleInput={handleInputChange}
            customClass={styles.billingZipContainer}
            customContainerStyles={styles.inputContainer}
          />
          {isSavePaymentMethodAllowed ? (
            <CheckBox
              label="Save my credit card info"
              onClick={() => {
                handleInputChange({
                  target: { name: 'saveCard', value: !inputs.saveCard }
                });
              }}
              name="saveCard"
              disabled={isSavePaymentMethodCheckedAndDisabled}
              isChecked={inputs.saveCard || isSavePaymentMethodCheckedAndDisabled}
              checkIconSrc={getCheckoutCheckIconSrc()}
            />
          ) : null}

          <div
            className={styles.buttonContainer}
            data-part={getCheckoutPartProps('legacy-add-card-submit')}
          >
            <Button
              name="btnSubmit"
              disabled={
                !(
                  inputs.cardHolderName &&
                  inputs.expirationDate &&
                  inputs.postcode &&
                  inputs.cvv &&
                  inputs.cardNumber
                )
              }
              handleSubmit={e => {
                handleSubmit(e);
                if (hasSavedCards) {
                  setView(views.savedCards);
                }
              }}
              label={submitLabel}
            />
          </div>
          {!hideCancel ? (
            <div
              className={stylesShared.footerActions}
              onClick={onCancelCc}
              onKeyDown={keyDownA11y(onCancelCc)}
              role="button"
              tabIndex={0}
              hidden={hideCancel}
              data-part={getCheckoutPartProps('legacy-change-payment-method')}
            >
              Change Payment Method
            </div>
          ) : null}
        </form>
      </div>
    </>
  );
};

PaymentInputCreditCardLegacy.propTypes = {
  ...propTypesComponentPaymentInput,
  inputsInitial: PropTypes.shape({
    securityCode: PropTypes.string,
    cardHolderName: PropTypes.string,
    expirationDate: PropTypes.string,
    postcode: PropTypes.string,
    saveCredentials: PropTypes.bool,
    cardNumber: PropTypes.string
  })
};

PaymentInputCreditCardLegacy.defaultProps = {
  inputsInitial: null
};
export default PaymentInputCreditCardLegacy;
